(function () {
  function parseOptions(element) {
    var rawOptions = element.getAttribute("data-swiper-options");

    if (!rawOptions) {
      return {};
    }

    try {
      return JSON.parse(rawOptions);
    } catch (error) {
      console.warn("Invalid Swiper options:", element, error);
      return {};
    }
  }

  function initSwipers(scope) {
    if (!window.Swiper) {
      return;
    }

    var root = scope || document;
    var swipers = root.querySelectorAll(
      "[data-swiper]:not([data-swiper-initialized])",
    );

    swipers.forEach(function (element) {
      var options = parseOptions(element);

      element.swiperInstance = new window.Swiper(element, options);
      element.setAttribute("data-swiper-initialized", "true");
    });
  }

  function createInfiniteCarousel(
    sectionSelector,
    direction = "left",
    options = {},
  ) {
    const {
      speed = 60,
      breakpoint,
      pauseWhenHidden = true,
      itemSelector = ".cloneable",
    } = options;

    const sections = document.querySelectorAll(sectionSelector);
    if (!sections.length) return () => {};

    const reducedMotionQuery = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    );
    if (reducedMotionQuery?.matches) return () => {};

    const destroyFns = [];

    const bindMediaQueryListener = (mediaQuery, handler) => {
      if (!mediaQuery) return () => {};

      if (typeof mediaQuery.addEventListener === "function") {
        mediaQuery.addEventListener("change", handler);
        return () => mediaQuery.removeEventListener("change", handler);
      }

      mediaQuery.addListener(handler);
      return () => mediaQuery.removeListener(handler);
    };

    const setupTrack = (section, track) => {
      if (typeof track.__marqueeDestroy === "function") {
        track.__marqueeDestroy();
      }

      const viewport = track.parentElement || section;
      const breakpointValue = Number(breakpoint);
      const breakpointQuery = Number.isFinite(breakpointValue)
        ? window.matchMedia(`(max-width: ${breakpointValue}px)`)
        : null;

      const initialTrackStyle = {
        transform: track.style.transform,
        willChange: track.style.willChange,
        overflow: track.style.overflow,
        width: track.style.width,
        flexWrap: track.style.flexWrap,
      };

      let active = false;
      let destroyed = false;
      let cycleWidth = 0;
      let position = 0;
      let rafId = null;
      let lastTime = 0;
      let resizeTimer = null;
      let resizeObserver = null;

      const getOriginalItems = () =>
        Array.from(track.children).filter(
          (child) =>
            child.matches?.(itemSelector) &&
            child.dataset.marqueeClone !== "true",
        );

      const removeClones = () => {
        track
          .querySelectorAll("[data-marquee-clone='true']")
          .forEach((clone) => clone.remove());
      };

      const restoreStyles = () => {
        track.style.transform = initialTrackStyle.transform;
        track.style.willChange = initialTrackStyle.willChange;
        track.style.overflow = initialTrackStyle.overflow;
        track.style.width = initialTrackStyle.width;
        track.style.flexWrap = initialTrackStyle.flexWrap;
      };

      const applyActiveStyles = () => {
        track.style.willChange = "transform";
        track.style.overflow = "visible";
        track.style.width = "max-content";
        track.style.flexWrap = "nowrap";
      };

      const measureCycleWidth = () => {
        const originals = getOriginalItems();
        if (!originals.length) return 0;

        const firstRect = originals[0].getBoundingClientRect();
        const lastRect = originals[originals.length - 1].getBoundingClientRect();

        return Math.max(lastRect.right - firstRect.left, 0);
      };

      const syncClones = () => {
        const originals = getOriginalItems();
        if (!originals.length) return false;

        removeClones();
        applyActiveStyles();

        cycleWidth = measureCycleWidth();
        if (!cycleWidth) return false;

        const requiredWidth =
          Math.max(viewport.getBoundingClientRect().width, cycleWidth) +
          cycleWidth;
        let cloneSets = 0;

        do {
          const fragment = document.createDocumentFragment();

          originals.forEach((item) => {
            const clone = item.cloneNode(true);
            clone.dataset.marqueeClone = "true";
            clone.setAttribute("aria-hidden", "true");
            fragment.appendChild(clone);
          });

          track.appendChild(fragment);
          cloneSets += 1;
        } while (track.scrollWidth < requiredWidth && cloneSets < 20);

        return true;
      };

      const applyTransform = () => {
        track.style.transform = `translate3d(${position}px, 0, 0)`;
      };

      const stop = () => {
        clearTimeout(resizeTimer);
        cancelAnimationFrame(rafId);
        rafId = null;
        lastTime = 0;
      };

      const step = (time) => {
        if (!active || !cycleWidth) return;

        if (!lastTime) lastTime = time;
        const dt = (time - lastTime) / 1000;
        lastTime = time;

        const delta = speed * dt;

        if (direction === "right") {
          position += delta;
          if (position >= 0) position -= cycleWidth;
        } else {
          position -= delta;
          if (Math.abs(position) >= cycleWidth) position += cycleWidth;
        }

        applyTransform();
        rafId = requestAnimationFrame(step);
      };

      const start = () => {
        if (!active || rafId !== null || !cycleWidth) return;
        if (pauseWhenHidden && document.hidden) return;

        lastTime = 0;
        rafId = requestAnimationFrame(step);
      };

      const refresh = () => {
        if (!active) return;

        stop();

        if (!syncClones()) {
          removeClones();
          restoreStyles();
          return;
        }

        position = direction === "right" ? -cycleWidth : 0;
        applyTransform();
        start();
      };

      const disable = () => {
        if (!active) return;

        active = false;
        stop();
        removeClones();
        restoreStyles();
      };

      const enable = () => {
        if (active) return;

        active = true;
        refresh();
      };

      const shouldEnable = () => !breakpointQuery || breakpointQuery.matches;

      const scheduleRefresh = () => {
        if (!active || destroyed) return;

        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          refresh();
        }, 120);
      };

      const onVisibilityChange = () => {
        if (!pauseWhenHidden || !active) return;

        if (document.hidden) stop();
        else start();
      };

      const onBreakpointChange = () => {
        if (shouldEnable()) enable();
        else disable();
      };

      const onWindowResize = () => {
        scheduleRefresh();
      };

      const removeBreakpointListener = bindMediaQueryListener(
        breakpointQuery,
        onBreakpointChange,
      );

      window.addEventListener("resize", onWindowResize);
      document.addEventListener("visibilitychange", onVisibilityChange);

      if (typeof ResizeObserver === "function") {
        resizeObserver = new ResizeObserver(() => {
          scheduleRefresh();
        });
        resizeObserver.observe(track);
        resizeObserver.observe(viewport);
      }

      if (document.fonts?.ready) {
        document.fonts.ready
          .then(() => {
            scheduleRefresh();
          })
          .catch(() => {});
      }

      if (shouldEnable()) enable();

      const destroy = () => {
        if (destroyed) return;
        destroyed = true;

        disable();
        removeBreakpointListener();
        window.removeEventListener("resize", onWindowResize);
        document.removeEventListener("visibilitychange", onVisibilityChange);
        resizeObserver?.disconnect();

        if (track.__marqueeDestroy === destroy) {
          delete track.__marqueeDestroy;
        }
      };

      track.__marqueeDestroy = destroy;
      return destroy;
    };

    sections.forEach((section) => {
      const tracks = new Set();

      section
        .querySelectorAll(`${itemSelector}:not([data-marquee-clone='true'])`)
        .forEach((item) => {
          if (item.parentElement) tracks.add(item.parentElement);
        });

      tracks.forEach((track) => {
        destroyFns.push(setupTrack(section, track));
      });
    });

    return () => destroyFns.forEach((destroy) => destroy());
  }

  function initInfiniteCarousels(scope) {
    var root = scope || document;
    var sections = [];

    if (
      root.matches &&
      root.matches(
        "[data-infinite-carousel]:not([data-infinite-carousel-initialized])",
      )
    ) {
      sections.push(root);
    }

    root
      .querySelectorAll(
        "[data-infinite-carousel]:not([data-infinite-carousel-initialized])",
      )
      .forEach(function (section) {
        sections.push(section);
      });

    sections.forEach(function (section) {
      var speed = parseFloat(section.getAttribute("data-carousel-speed")) || 70;
      var direction =
        section.getAttribute("data-carousel-direction") || "left";

      section.infiniteCarouselDestroy = createInfiniteCarousel(
        "#" + section.id,
        direction,
        {
          speed: speed,
        },
      );
      section.setAttribute("data-infinite-carousel-initialized", "true");
    });
  }

  function initQuantityControls(scope) {
    var root = scope || document;
    var quantityControls = root.querySelectorAll(
      "[data-quantity-wrapper]:not([data-quantity-initialized])",
    );

    quantityControls.forEach(function (control) {
      control.addEventListener("click", function (event) {
        var button = event.target.closest(
          "[data-quantity-minus], [data-quantity-plus]",
        );

        if (!button || !control.contains(button)) {
          return;
        }

        var input = control.querySelector("[data-quantity-input]");

        if (!input) {
          return;
        }

        var min = parseInt(input.getAttribute("min"), 10) || 1;
        var currentValue = parseInt(input.value, 10) || min;
        var nextValue = button.hasAttribute("data-quantity-plus")
          ? currentValue + 1
          : currentValue - 1;

        input.value = Math.max(min, nextValue);
        input.dispatchEvent(new Event("change", { bubbles: true }));
      });

      control.setAttribute("data-quantity-initialized", "true");
    });
  }

  function initProductGalleries(scope) {
    var root = scope || document;
    var galleries = root.querySelectorAll(
      "[data-product-gallery]:not([data-product-gallery-initialized])",
    );

    galleries.forEach(function (gallery) {
      gallery.addEventListener("click", function (event) {
        var thumbnail = event.target.closest(
          "[data-product-gallery-thumbnail]",
        );

        if (!thumbnail || !gallery.contains(thumbnail)) {
          return;
        }

        var mainImage = gallery.querySelector("[data-product-gallery-main]");

        if (!mainImage) {
          return;
        }

        var imageSrc = thumbnail.getAttribute("data-gallery-image-src");
        var imageSrcset = thumbnail.getAttribute("data-gallery-image-srcset");
        var imageSizes = thumbnail.getAttribute("data-gallery-image-sizes");
        var imageAlt = thumbnail.getAttribute("data-gallery-image-alt");

        gallery
          .querySelectorAll("[data-product-gallery-thumbnail]")
          .forEach(function (button) {
            button.classList.remove("is-active");
            button.removeAttribute("aria-current");
          });

        thumbnail.classList.add("is-active");
        thumbnail.setAttribute("aria-current", "true");

        function applyMainImageSwap() {
          if (imageSrcset) {
            mainImage.setAttribute("srcset", imageSrcset);
          }

          if (imageSizes) {
            mainImage.setAttribute("sizes", imageSizes);
          }

          if (imageSrc) {
            mainImage.setAttribute("src", imageSrc);
          }

          if (imageAlt !== null) {
            mainImage.setAttribute("alt", imageAlt);
          }

          window.requestAnimationFrame(function () {
            window.requestAnimationFrame(function () {
              mainImage.classList.remove("is-changing");
            });
          });
        }

        function swapMainImage() {
          mainImage.classList.add("is-changing");

          window.setTimeout(applyMainImageSwap, 220);
        }

        if (!imageSrc) {
          swapMainImage();
          return;
        }

        if (mainImage.getAttribute("src") === imageSrc) {
          return;
        }

        var preloadImage = new Image();

        preloadImage.onload = swapMainImage;
        preloadImage.onerror = swapMainImage;
        preloadImage.src = imageSrc;
      });

      gallery.setAttribute("data-product-gallery-initialized", "true");
    });
  }

  function initProductRecommendations(scope) {
    var root = scope || document;
    var recommendationLoaders = root.querySelectorAll(
      "[data-product-recommendations]:not([data-product-recommendations-initialized])",
    );

    recommendationLoaders.forEach(function (loader) {
      var recommendationsUrl = loader.getAttribute(
        "data-recommendations-url",
      );
      var fallbackUrl = loader.getAttribute(
        "data-recommendations-fallback-url",
      );
      var replaceTarget = loader.hasAttribute(
        "data-recommendations-replace-target",
      );
      var targetSelector = loader.getAttribute("data-recommendations-target");
      var target = targetSelector
        ? document.querySelector(targetSelector)
        : loader;

      loader.setAttribute("data-product-recommendations-initialized", "true");

      if (!recommendationsUrl || !target) {
        return;
      }

      function fetchRecommendations(url) {
        return window
          .fetch(url, {
            headers: {
              "X-Requested-With": "XMLHttpRequest",
            },
          })
          .then(function (response) {
            if (!response.ok) {
              throw new Error("Unable to load product recommendations.");
            }

            return response.text();
          })
          .then(function (sectionHtml) {
            var parsedDocument = new DOMParser().parseFromString(
              sectionHtml,
              "text/html",
            );
            var content = parsedDocument.querySelector(
              "[data-product-recommendations-content]",
            );

            if (
              (!content || !content.innerHTML.trim()) &&
              fallbackUrl &&
              url !== fallbackUrl
            ) {
              return fetchRecommendations(fallbackUrl);
            }

            return content;
          });
      }

      fetchRecommendations(recommendationsUrl)
        .then(function (content) {
          if (!content || !content.innerHTML.trim()) {
            return;
          }

          var initializedScope = target;

          if (replaceTarget) {
            target.replaceWith(content);
            initializedScope = content;
          } else {
            target.replaceChildren(content);
            target.classList.remove("d-none");
          }

          initSwipers(initializedScope);
          initQuantityControls(initializedScope);
          initCartAddForms(initializedScope);
          initProductCardVariants(initializedScope);

          document.dispatchEvent(
            new CustomEvent("theme:product-recommendations:loaded", {
              detail: {
                target: initializedScope,
              },
            }),
          );
        })
        .catch(function (error) {
          console.error(error);
        });
    });
  }

  function setCollectionUpdating(section, isUpdating) {
    section.classList.toggle("is-updating", isUpdating);
    section.setAttribute("aria-busy", isUpdating ? "true" : "false");

    var loadingIndicator = section.querySelector("[data-collection-loading]");

    if (loadingIndicator) {
      loadingIndicator.setAttribute(
        "aria-hidden",
        isUpdating ? "false" : "true",
      );
    }
  }

  /*
   * Shopify can return a title-ascending sequence that is not a natural
   * numeric sequence for product titles. Liquid only exposes the current
   * paginated page, so sorting there would make page boundaries incorrect.
   *
   * For collections explicitly set to A-Z, collect at most 100 already
   * filtered cards through the section renderer, sort them in the browser,
   * and then render the matching logical page. Larger or failed requests keep
   * Shopify's native order without changing the visible listing.
   */
  var collectionNaturalSortValue = "title-ascending";
  var collectionNaturalSortMaxProducts = 100;
  var collectionNaturalSortMaxConcurrentRequests = 4;
  var collectionNaturalSortCache = new Map();

  function getCollectionNaturalSortGrid(section) {
    return section.querySelector("[data-collection-product-grid]");
  }

  function getCollectionNaturalSortMetadata(grid) {
    var productCount = Number(
      grid.getAttribute("data-collection-product-count"),
    );
    var currentPage = Number(
      grid.getAttribute("data-collection-product-page"),
    );
    var pageSize = Number(
      grid.getAttribute("data-collection-product-page-size"),
    );
    var pageCount = Number(
      grid.getAttribute("data-collection-product-pages"),
    );

    if (
      !Number.isInteger(productCount) ||
      !Number.isInteger(currentPage) ||
      !Number.isInteger(pageSize) ||
      !Number.isInteger(pageCount) ||
      productCount < 1 ||
      currentPage < 1 ||
      pageSize < 1 ||
      pageCount < 1
    ) {
      return null;
    }

    return {
      productCount: productCount,
      currentPage: currentPage,
      pageSize: pageSize,
      pageCount: pageCount,
    };
  }

  function getCollectionNaturalSortCacheKey(url) {
    var cacheUrl = new URL(url, window.location.origin);

    cacheUrl.searchParams.delete("page");
    cacheUrl.searchParams.delete("section_id");
    cacheUrl.searchParams.sort();

    return cacheUrl.pathname + cacheUrl.search;
  }

  function shouldApplyCollectionNaturalSort(section) {
    var sortSelect = section.querySelector("[data-collection-sort]");

    return Boolean(
      section.getAttribute("data-listing-type") === "collection" &&
        sortSelect &&
        sortSelect.value === collectionNaturalSortValue,
    );
  }

  function serializeCollectionNaturalSortColumn(column) {
    var columnClone = column.cloneNode(true);

    columnClone
      .querySelectorAll("[data-cart-add-form-initialized]")
      .forEach(function (form) {
        form.removeAttribute("data-cart-add-form-initialized");
      });

    return columnClone.outerHTML;
  }

  function getCollectionNaturalSortRecords(grid) {
    return Array.from(grid.children)
      .map(function (column) {
        var card = column.querySelector("[data-collection-product-card]");

        if (!card) {
          return null;
        }

        return {
          id: card.getAttribute("data-collection-product-id") || "",
          title: card.getAttribute("data-collection-product-title") || "",
          html: serializeCollectionNaturalSortColumn(column),
        };
      })
      .filter(Boolean);
  }

  function getCollectionNaturalSortPageUrl(url, page, sectionId) {
    var pageUrl = new URL(url, window.location.origin);

    pageUrl.searchParams.set("page", page);
    pageUrl.searchParams.set("section_id", sectionId);

    return pageUrl.toString();
  }

  function mapWithConcurrency(values, limit, callback) {
    var results = new Array(values.length);
    var nextIndex = 0;

    function runNext() {
      var index = nextIndex;

      nextIndex += 1;

      if (index >= values.length) {
        return Promise.resolve();
      }

      return Promise.resolve(callback(values[index], index)).then(function (
        result,
      ) {
        results[index] = result;
        return runNext();
      });
    }

    return Promise.all(
      Array.from({ length: Math.min(limit, values.length) }, runNext),
    ).then(function () {
      return results;
    });
  }

  function fetchCollectionNaturalSortPage(url, metadata) {
    return window
      .fetch(url, {
        headers: {
          "X-Requested-With": "XMLHttpRequest",
        },
      })
      .then(function (response) {
        if (!response.ok) {
          throw new Error("Unable to load collection products for sorting.");
        }

        return response.text();
      })
      .then(function (sectionHtml) {
        var parsedDocument = new DOMParser().parseFromString(
          sectionHtml,
          "text/html",
        );
        var grid = parsedDocument.querySelector(
          "[data-collection-product-grid]",
        );
        var pageMetadata = grid && getCollectionNaturalSortMetadata(grid);

        if (
          !grid ||
          !pageMetadata ||
          pageMetadata.productCount !== metadata.productCount ||
          pageMetadata.pageSize !== metadata.pageSize ||
          pageMetadata.pageCount !== metadata.pageCount
        ) {
          throw new Error("Collection products changed while sorting.");
        }

        return getCollectionNaturalSortRecords(grid);
      });
  }

  function sortCollectionNaturalRecords(records) {
    var language = document.documentElement.lang || undefined;
    var collator = new Intl.Collator(language, {
      numeric: true,
      sensitivity: "base",
    });

    function normalizeTitle(title) {
      // Treat accidental duplicate spaces as one when building the sort key.
      return title.replace(/\s+/g, " ").trim();
    }

    return records.sort(function (left, right) {
      var titleComparison = collator.compare(
        normalizeTitle(left.title),
        normalizeTitle(right.title),
      );

      if (titleComparison !== 0) {
        return titleComparison;
      }

      return collator.compare(left.id, right.id);
    });
  }

  function loadCollectionNaturalSortRecords(section, url, grid, metadata) {
    var sectionId = section.getAttribute("data-section-id");
    var pageRecords = new Array(metadata.pageCount);
    var pagesToLoad = [];

    if (!sectionId) {
      return Promise.reject(
        new Error("Collection section is missing its section identifier."),
      );
    }

    pageRecords[metadata.currentPage - 1] = getCollectionNaturalSortRecords(
      grid,
    );

    for (var page = 1; page <= metadata.pageCount; page += 1) {
      if (page !== metadata.currentPage) {
        pagesToLoad.push(page);
      }
    }

    return mapWithConcurrency(
      pagesToLoad,
      collectionNaturalSortMaxConcurrentRequests,
      function (page) {
        return fetchCollectionNaturalSortPage(
          getCollectionNaturalSortPageUrl(url, page, sectionId),
          metadata,
        ).then(function (records) {
          pageRecords[page - 1] = records;
        });
      },
    ).then(function () {
      var records = pageRecords.flat();

      if (records.length !== metadata.productCount) {
        throw new Error("Collection products could not be fully loaded.");
      }

      return sortCollectionNaturalRecords(records);
    });
  }

  function createCollectionNaturalSortColumn(html) {
    var template = document.createElement("template");

    template.innerHTML = html;

    return template.content.firstElementChild;
  }

  function renderCollectionNaturalSortPage(grid, records, metadata) {
    var start = (metadata.currentPage - 1) * metadata.pageSize;
    var pageRecords = records.slice(start, start + metadata.pageSize);
    var fragment = document.createDocumentFragment();

    pageRecords.forEach(function (record) {
      var column = createCollectionNaturalSortColumn(record.html);

      if (column) {
        fragment.appendChild(column);
      }
    });

    if (pageRecords.length !== Math.min(metadata.pageSize, records.length - start)) {
      throw new Error("Collection products could not be rendered.");
    }

    grid.replaceChildren(fragment);
    grid.setAttribute("data-collection-natural-sort-applied", "true");
  }

  function applyCollectionNaturalSort(section, url) {
    var grid;
    var metadata;
    var cacheKey;

    if (
      !shouldApplyCollectionNaturalSort(section) ||
      section.hasAttribute("data-collection-natural-sort-loading")
    ) {
      return Promise.resolve(false);
    }

    grid = getCollectionNaturalSortGrid(section);
    metadata = grid && getCollectionNaturalSortMetadata(grid);

    if (!grid || !metadata || metadata.productCount > collectionNaturalSortMaxProducts) {
      return Promise.resolve(false);
    }

    cacheKey = getCollectionNaturalSortCacheKey(url);
    section.setAttribute("data-collection-natural-sort-loading", "true");
    setCollectionUpdating(section, true);

    function renderRecords(records) {
      renderCollectionNaturalSortPage(grid, records, metadata);
      initCartAddForms(grid);
      initProductCardVariants(grid);
      return true;
    }

    if (collectionNaturalSortCache.has(cacheKey)) {
      var cachedSort = collectionNaturalSortCache.get(cacheKey);

      if (
        cachedSort.productCount === metadata.productCount &&
        cachedSort.pageSize === metadata.pageSize &&
        cachedSort.pageCount === metadata.pageCount
      ) {
        try {
          return Promise.resolve(
            renderRecords(cachedSort.records),
          ).finally(function () {
            section.removeAttribute("data-collection-natural-sort-loading");

            if (!section.hasAttribute("data-collection-updating")) {
              setCollectionUpdating(section, false);
            }
          });
        } catch (error) {
          collectionNaturalSortCache.delete(cacheKey);
        }
      } else {
        collectionNaturalSortCache.delete(cacheKey);
      }
    }

    return loadCollectionNaturalSortRecords(section, url, grid, metadata)
      .then(function (records) {
        collectionNaturalSortCache.set(cacheKey, {
          productCount: metadata.productCount,
          pageSize: metadata.pageSize,
          pageCount: metadata.pageCount,
          records: records,
        });
        return renderRecords(records);
      })
      .catch(function () {
        return false;
      })
      .finally(function () {
        section.removeAttribute("data-collection-natural-sort-loading");

        if (!section.hasAttribute("data-collection-updating")) {
          setCollectionUpdating(section, false);
        }
      });
  }

  function updateCollection(section, url, options) {
    var settings = options || {};
    var sectionId = section.getAttribute("data-section-id");
    var listingType =
      section.getAttribute("data-listing-type") || "collection";
    var browserUrl = new URL(url, window.location.origin);
    var requestUrl = new URL(browserUrl.toString());
    var activeSection = section;

    if (!sectionId || section.hasAttribute("data-collection-updating")) {
      return Promise.resolve();
    }

    requestUrl.searchParams.set("section_id", sectionId);
    section.setAttribute("data-collection-updating", "true");
    setCollectionUpdating(section, true);

    return window
      .fetch(requestUrl.toString(), {
        headers: {
          "X-Requested-With": "XMLHttpRequest",
        },
      })
      .then(function (response) {
        if (!response.ok) {
          throw new Error("Unable to update product listing.");
        }

        return response.text();
      })
      .then(function (sectionHtml) {
        var parsedDocument = new DOMParser().parseFromString(
          sectionHtml,
          "text/html",
        );
        var nextSection = parsedDocument.querySelector(
          "[data-collection-section]",
        );

        if (!nextSection) {
          throw new Error("Rendered product listing markup was not found.");
        }

        section.replaceWith(nextSection);
        activeSection = nextSection;
        activeSection.setAttribute("data-collection-updating", "true");
        setCollectionUpdating(activeSection, true);
        initCollectionPages(nextSection);
        initCartAddForms(nextSection);
        initProductCardVariants(nextSection);

        return applyCollectionNaturalSort(
          nextSection,
          browserUrl.toString(),
        ).then(function () {
          if (settings.updateHistory !== false) {
            window.history.pushState({}, "", browserUrl.toString());
          }

          if (settings.scrollToResults) {
            nextSection.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }

          document.dispatchEvent(
            new CustomEvent("theme:" + listingType + ":updated", {
              detail: {
                section: nextSection,
                url: browserUrl.toString(),
              },
            }),
          );
        });
      })
      .catch(function (error) {
        console.error(error);

        if (settings.fallbackToNavigation !== false) {
          window.location.assign(browserUrl.toString());
        }
      })
      .finally(function () {
        if (activeSection.isConnected) {
          activeSection.removeAttribute("data-collection-updating");
          setCollectionUpdating(activeSection, false);
        }
      });
  }

  function initCollectionPages(scope) {
    var root = scope || document;
    var sections = [];

    if (root.matches && root.matches("[data-collection-section]")) {
      sections.push(root);
    }

    root
      .querySelectorAll(
        "[data-collection-section]:not([data-collection-initialized])",
      )
      .forEach(function (section) {
        sections.push(section);
      });

    sections.forEach(function (section) {
      if (section.hasAttribute("data-collection-initialized")) {
        return;
      }

      section.addEventListener("click", function (event) {
        var link = event.target.closest("[data-collection-link]");

        if (
          !link ||
          !section.contains(link) ||
          link.getAttribute("aria-disabled") === "true" ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey
        ) {
          return;
        }

        event.preventDefault();

        var nextUrl = link.href;

        if (link.hasAttribute("data-collection-single-filter")) {
          var singleFilterUrl = new URL(window.location.href);
          var filterParam = link.getAttribute("data-filter-param");
          var filterValue = link.getAttribute("data-filter-value");

          if (filterParam) {
            singleFilterUrl.searchParams.delete(filterParam);

            if (
              !link.hasAttribute("data-filter-active") &&
              filterValue !== null
            ) {
              singleFilterUrl.searchParams.set(filterParam, filterValue);
            }

            singleFilterUrl.searchParams.delete("page");
            nextUrl = singleFilterUrl.toString();
          }
        }

        updateCollection(section, nextUrl, {
          scrollToResults: Boolean(
            link.closest("[data-collection-pagination]"),
          ),
        });
      });

      section.addEventListener("change", function (event) {
        var sortSelect = event.target.closest("[data-collection-sort]");

        if (!sortSelect || !section.contains(sortSelect)) {
          return;
        }

        var nextUrl = new URL(window.location.href);

        nextUrl.searchParams.set("sort_by", sortSelect.value);
        nextUrl.searchParams.delete("page");
        updateCollection(section, nextUrl.toString());
      });

      section.addEventListener("submit", function (event) {
        var filterForm = event.target.closest("[data-collection-filter-form]");

        if (!filterForm || !section.contains(filterForm)) {
          return;
        }

        event.preventDefault();

        var nextUrl = new URL(window.location.href);
        var formData = new FormData(filterForm);

        formData.forEach(function (value, key) {
          if (String(value).trim()) {
            nextUrl.searchParams.set(key, value);
          } else {
            nextUrl.searchParams.delete(key);
          }
        });

        nextUrl.searchParams.delete("page");
        updateCollection(section, nextUrl.toString());
      });

      section.setAttribute("data-collection-initialized", "true");
    });
  }

  function getCartDrawer() {
    return document.querySelector("[data-cart-offcanvas]");
  }

  function openCart() {
    var drawer = getCartDrawer();

    if (!drawer || !window.bootstrap || !window.bootstrap.Offcanvas) {
      return false;
    }

    window.bootstrap.Offcanvas.getOrCreateInstance(drawer).show();
    return true;
  }

  function closeCart() {
    var drawer = getCartDrawer();

    if (!drawer || !window.bootstrap || !window.bootstrap.Offcanvas) {
      return false;
    }

    window.bootstrap.Offcanvas.getOrCreateInstance(drawer).hide();
    return true;
  }

  function getCartJsonUrl() {
    var root =
      window.Shopify && window.Shopify.routes
        ? window.Shopify.routes.root
        : "/";

    return root + "cart.js";
  }

  function getCartFormJsonUrl(action) {
    var url = new URL(action, window.location.origin);

    if (!url.pathname.endsWith(".js")) {
      url.pathname = url.pathname.replace(/\/$/, "") + ".js";
    }

    return url.toString();
  }

  function updateCartCount(itemCount) {
    document.querySelectorAll("[data-cart-count]").forEach(function (count) {
      count.textContent = itemCount;
      count.classList.toggle("d-none", itemCount === 0);
    });

    document.querySelectorAll("[data-cart-trigger]").forEach(function (trigger) {
      var labelTemplate = trigger.getAttribute("data-cart-label-template");

      if (labelTemplate) {
        trigger.setAttribute(
          "aria-label",
          labelTemplate.replace("__COUNT__", itemCount),
        );
      }
    });
  }

  function fetchCartState() {
    return window
      .fetch(getCartJsonUrl(), {
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
      })
      .then(function (response) {
        if (!response.ok) {
          throw new Error("Unable to load cart state.");
        }

        return response.json();
      });
  }

  function setCartUpdating(isUpdating) {
    var drawer = getCartDrawer();

    if (!drawer) {
      return;
    }

    drawer.classList.toggle("is-updating", isUpdating);
    drawer.setAttribute("aria-busy", isUpdating ? "true" : "false");

    var loadingIndicator = drawer.querySelector("[data-cart-loading]");

    if (loadingIndicator) {
      loadingIndicator.setAttribute(
        "aria-hidden",
        isUpdating ? "false" : "true",
      );
    }
  }

  function refreshCart(cartState) {
    var drawer = getCartDrawer();

    if (!drawer) {
      return Promise.reject(new Error("Cart drawer was not found."));
    }

    var sectionId = drawer.getAttribute("data-cart-section-id");

    if (!sectionId) {
      return Promise.reject(new Error("Cart section id was not found."));
    }

    setCartUpdating(true);

    var sectionUrl = new URL(window.location.href);
    sectionUrl.searchParams.set("section_id", sectionId);
    sectionUrl.searchParams.set("_", Date.now());

    var cartPromise = cartState
      ? Promise.resolve(cartState)
      : fetchCartState();

    return Promise.all([
      cartPromise,
      window.fetch(sectionUrl.toString(), {
        headers: {
          "X-Requested-With": "XMLHttpRequest",
        },
      }),
    ])
      .then(function (results) {
        var nextCartState = results[0];
        var sectionResponse = results[1];

        if (!sectionResponse.ok) {
          throw new Error("Unable to render cart drawer.");
        }

        return sectionResponse.text().then(function (sectionHtml) {
          var parsedDocument = new DOMParser().parseFromString(
            sectionHtml,
            "text/html",
          );
          var nextDrawer = parsedDocument.querySelector(
            "[data-cart-offcanvas]",
          );

          if (!nextDrawer) {
            throw new Error("Rendered cart drawer markup was not found.");
          }

          drawer.innerHTML = nextDrawer.innerHTML;
          updateCartCount(nextCartState.item_count);
          initQuantityControls(drawer);
          initCartForms(drawer);

          document.dispatchEvent(
            new CustomEvent("theme:cart:updated", {
              detail: {
                cart: nextCartState,
              },
            }),
          );

          return nextCartState;
        });
      })
      .finally(function () {
        setCartUpdating(false);
      });
  }

  function setCartFormBusy(form, isBusy) {
    form.toggleAttribute("data-cart-form-submitting", isBusy);
    form.setAttribute("aria-busy", isBusy ? "true" : "false");

    // Card buttons swap their decorative icon until add and cart refresh finish.
    var cardLoader = form.querySelector("[data-product-card-loading]");
    if (cardLoader) {
      var cardButton = cardLoader.closest("button");
      var cardIcon = cardButton.querySelector(".carma-product-card__cart-icon");
      cardLoader.classList.toggle("d-none", !isBusy);
      if (cardIcon) cardIcon.classList.toggle("d-none", isBusy);
      cardButton.setAttribute("aria-busy", isBusy ? "true" : "false");
    }

    form.querySelectorAll("button, input, select").forEach(function (control) {
      control.disabled = isBusy;
    });
  }

  function submitCartForm(form) {
    if (form.hasAttribute("data-cart-form-submitting")) {
      return;
    }

    var formData = new FormData(form);

    setCartFormBusy(form, true);
    setCartUpdating(true);

    window
      .fetch(getCartFormJsonUrl(form.action), {
        method: "POST",
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: formData,
      })
      .then(function (response) {
        if (!response.ok) {
          return response.json().then(function (error) {
            throw new Error(error.description || "Unable to update cart.");
          });
        }

        return response.json();
      })
      .then(refreshCart)
      .catch(function (error) {
        console.error(error);
        setCartFormBusy(form, false);
        setCartUpdating(false);

        document.dispatchEvent(
          new CustomEvent("theme:cart:error", {
            detail: {
              error: error,
            },
          }),
        );
      });
  }

  function initCartForms(scope) {
    var root = scope || document;
    var forms = root.querySelectorAll(
      "[data-cart-form]:not([data-cart-form-initialized])",
    );

    forms.forEach(function (form) {
      var input = form.querySelector("[data-cart-quantity-input]");

      if (input) {
        input.addEventListener("change", function () {
          if (!input.checkValidity()) {
            input.reportValidity();
            return;
          }

          form.requestSubmit();
        });
      }

      form.addEventListener("submit", function (event) {
        event.preventDefault();
        submitCartForm(form);
      });

      form.setAttribute("data-cart-form-initialized", "true");
    });
  }

  function initCartAddForms(scope) {
    var root = scope || document;
    var forms = root.querySelectorAll(
      "[data-cart-add-form]:not([data-cart-add-form-initialized])",
    );

    forms.forEach(function (form) {
      form.addEventListener("submit", function (event) {
        var submitter = event.submitter;

        if (form.closest("[data-product-detail-updating]")) {
          event.preventDefault();
          return;
        }

        if (
          submitter &&
          (submitter.name === "return_to" || submitter.name === "checkout")
        ) {
          return;
        }

        event.preventDefault();

        if (form.hasAttribute("data-cart-form-submitting")) {
          return;
        }

        var formData = new FormData(form);

        setCartFormBusy(form, true);
        setCartUpdating(true);

        window
          .fetch(getCartFormJsonUrl(form.action), {
            method: "POST",
            headers: {
              Accept: "application/json",
              "X-Requested-With": "XMLHttpRequest",
            },
            body: formData,
          })
          .then(function (response) {
            if (!response.ok) {
              return response.json().then(function (error) {
                throw new Error(error.description || "Unable to add item.");
              });
            }

            return response.json();
          })
          .then(function () {
            return refreshCart();
          })
          .then(function () {
            openCart();
            setCartFormBusy(form, false);
          })
          .catch(function (error) {
            console.error(error);
            setCartFormBusy(form, false);
            setCartUpdating(false);

            document.dispatchEvent(
              new CustomEvent("theme:cart:error", {
                detail: {
                  error: error,
                },
              }),
            );
          });
      });

      form.setAttribute("data-cart-add-form-initialized", "true");
    });
  }

  function initProductDetailVariants(scope) {
    var root = scope || document;
    var selects = root.querySelectorAll(
      "[data-product-detail-variant-select]:not([data-product-detail-variant-initialized])",
    );

    selects.forEach(function (select) {
      var requestNumber = 0;
      var requestController;

      select.addEventListener("change", function () {
        if (!select.value) return;

        var productDetail = select.closest("[data-product-detail]");
        var sectionId = select.getAttribute("data-product-detail-section-id");
        var variantId = select.value;
        var variantUrl = new URL(window.location.href);
        variantUrl.searchParams.set("variant", variantId);
        variantUrl.searchParams.delete("section_id");

        if (!productDetail || !sectionId) {
          window.location.assign(variantUrl.toString());
          return;
        }

        var currentRequest = ++requestNumber;
        if (requestController) requestController.abort();
        requestController = new AbortController();

        var requestUrl = new URL(variantUrl.toString());
        requestUrl.searchParams.set("section_id", sectionId);
        productDetail.setAttribute("data-product-detail-updating", "true");
        productDetail.setAttribute("aria-busy", "true");
        select.closest("form")
          .querySelectorAll('button[type="submit"]')
          .forEach(function (button) {
            button.disabled = true;
          });

        // Let Liquid keep image, prices, installments and availability in sync.
        window
          .fetch(requestUrl.toString(), {
            headers: { "X-Requested-With": "XMLHttpRequest" },
            signal: requestController.signal,
          })
          .then(function (response) {
            if (!response.ok) throw new Error("Unable to load product variant.");
            return response.text();
          })
          .then(function (html) {
            if (currentRequest !== requestNumber || !productDetail.isConnected) {
              return;
            }

            var parsedDocument = new DOMParser().parseFromString(
              html,
              "text/html",
            );
            var nextDetail = parsedDocument.querySelector("[data-product-detail]");
            var nextSelect = nextDetail && nextDetail.querySelector(
              "[data-product-detail-variant-select]",
            );
            if (!nextSelect || nextSelect.value !== variantId) {
              throw new Error("Selected variant was not rendered.");
            }

            var quantity = productDetail.querySelector("[data-quantity-input]");
            var nextQuantity = nextDetail.querySelector("[data-quantity-input]");
            if (quantity && nextQuantity) nextQuantity.value = quantity.value;
            var focusedId = productDetail.contains(document.activeElement)
              ? document.activeElement.id
              : "";
            var complementary = productDetail.querySelector('[id^="CarmaComplementary-"]');
            var nextComplementary = nextDetail.querySelector('[id^="CarmaComplementary-"]');
            var preserveComplementary = complementary && nextComplementary &&
              complementary.id === nextComplementary.id;

            productDetail.querySelectorAll("[data-swiper]").forEach(function (element) {
              if (preserveComplementary && complementary.contains(element)) return;
              if (element.swiperInstance) element.swiperInstance.destroy(true, true);
            });
            productDetail.replaceWith(nextDetail);
            // Keep the recommendation loader's target and existing controls alive.
            if (preserveComplementary) nextComplementary.replaceWith(complementary);
            initSwipers(nextDetail);
            initQuantityControls(nextDetail);
            initProductGalleries(nextDetail.parentElement);
            initCartAddForms(nextDetail);
            initProductDetailVariants(nextDetail);
            window.history.replaceState(
              window.history.state,
              "",
              variantUrl.toString(),
            );

            var nextFocus = focusedId && document.getElementById(focusedId);
            if (nextFocus && nextDetail.contains(nextFocus)) {
              nextFocus.focus({ preventScroll: true });
            }
            document.dispatchEvent(
              new CustomEvent("theme:product:variant:updated", {
                detail: {
                  variantId: variantId,
                  productDetail: nextDetail,
                  url: variantUrl.toString(),
                },
              }),
            );
          })
          .catch(function (error) {
            if (
              error.name === "AbortError" ||
              currentRequest !== requestNumber ||
              !productDetail.isConnected
            ) {
              return;
            }
            console.error(error);
            window.location.assign(variantUrl.toString());
          });
      });

      select.setAttribute("data-product-detail-variant-initialized", "true");
    });
  }

  function initProductCardVariants(scope) {
    var root = scope || document;
    var cards = root.querySelectorAll(
      "[data-product-card]:not([data-product-card-initialized])",
    );

    cards.forEach(function (card) {
      var variants;

      try {
        variants = JSON.parse(card.getAttribute("data-product-variants") || "[]");
      } catch (error) {
        console.warn("Invalid product card variants:", card, error);
        return;
      }

      if (!variants.length) {
        return;
      }

      var optionGroups = Array.from(
        card.querySelectorAll("[data-product-card-option]"),
      ).sort(function (firstGroup, secondGroup) {
        return (
          Number(firstGroup.getAttribute("data-product-card-option-position")) -
          Number(secondGroup.getAttribute("data-product-card-option-position"))
        );
      });
      var hiddenVariantInput = card.querySelector(
        "[data-product-card-variant-id]",
      );
      var addButton = card.querySelector('[data-product-card-form] button[type="submit"]');
      var priceElement = card.querySelector("[data-product-card-price]");
      var installmentElement = card.querySelector("[data-product-card-installment]");
      var monthlyPriceElement = card.querySelector("[data-product-card-monthly-price]");
      var compareAtElement = card.querySelector(
        "[data-product-card-compare-at]",
      );
      var savingsElement = card.querySelector("[data-product-card-savings]");
      var productImage = card.querySelector(".carma-product-card__image");
      var productLinks = card.querySelectorAll("[data-product-card-link]");
      var productUrl = card.getAttribute("data-product-url") || "";

      function getOptionValue(group) {
        var checkedInput = group.querySelector(
          "[data-product-card-option-value]:checked",
        );

        return checkedInput ? checkedInput.value : "";
      }

      function findSelectedVariant() {
        // Product cards expose only multi-value color options. Other options stay
        // hidden so cards keep a consistent height; choose an available variant
        // that matches the visible color and let the product page handle the rest.
        var matchingVariants = variants.filter(function (variant) {
          return optionGroups.every(function (group) {
            var optionPosition = Number(
              group.getAttribute("data-product-card-option-position"),
            );

            return variant.options[optionPosition] === getOptionValue(group);
          });
        });

        return (
          matchingVariants.find(function (variant) {
            return variant.available;
          }) ||
          matchingVariants[0] ||
          variants.find(function (variant) {
            return variant.available;
          }) ||
          variants[0]
        );
      }

      function updateProductCard() {
        var selectedVariant = findSelectedVariant();

        if (!selectedVariant) {
          return;
        }

        if (hiddenVariantInput) {
          hiddenVariantInput.value = selectedVariant.id;
          hiddenVariantInput.setAttribute("value", selectedVariant.id);
        }

        if (addButton) {
          addButton.disabled = !selectedVariant.available;
        }

        if (priceElement) {
          priceElement.textContent = selectedVariant.price;
        }

        if (monthlyPriceElement) {
          monthlyPriceElement.textContent = selectedVariant.monthlyPrice || "";
        }

        if (installmentElement) {
          var showInstallments = selectedVariant.showInstallments === true;
          installmentElement.classList.toggle("d-none", !showInstallments);
          installmentElement.classList.toggle("d-flex", showInstallments);
        }

        if (compareAtElement) {
          var hasCompareAtPrice = Number(selectedVariant.savingsPercentage) > 0;
          compareAtElement.textContent = selectedVariant.compareAtPrice || "";
          compareAtElement.classList.toggle("d-none", !hasCompareAtPrice);
        }

        if (savingsElement) {
          var savingsPercentage = Number(selectedVariant.savingsPercentage);
          var hasSavings = Number.isFinite(savingsPercentage) && savingsPercentage > 0;
          var savingsFormat =
            savingsElement.getAttribute("data-product-card-savings-format") || "";

          if (hasSavings && savingsFormat) {
            savingsElement.textContent = savingsFormat.replace(
              "__percentage__",
              savingsPercentage,
            );
          }

          savingsElement.classList.toggle("d-none", !hasSavings);
        }

        if (productImage && selectedVariant.image) {
          productImage.src = selectedVariant.image;
          productImage.removeAttribute("srcset");
        }

        if (productUrl) {
          var variantUrl = new URL(productUrl, window.location.origin);

          variantUrl.searchParams.set("variant", selectedVariant.id);

          productLinks.forEach(function (link) {
            link.href = variantUrl.pathname + variantUrl.search + variantUrl.hash;
          });
        }
      }

      optionGroups.forEach(function (group) {
        group.addEventListener("change", updateProductCard);
      });

      card.setAttribute("data-product-card-initialized", "true");
      updateProductCard();
    });
  }

  document.addEventListener("DOMContentLoaded", function () {
    initSwipers(document);
    initInfiniteCarousels(document);
    initQuantityControls(document);
    initProductGalleries(document);
    initProductRecommendations(document);
    initCollectionPages(document);
    initCartForms(document);
    initCartAddForms(document);
    initProductDetailVariants(document);
    var collectionSection = document.querySelector("[data-collection-section]");

    if (collectionSection) {
      applyCollectionNaturalSort(collectionSection, window.location.href);
    }
    initProductCardVariants(document);
  });

  document.addEventListener("shopify:section:load", function (event) {
    initSwipers(event.target);
    initInfiniteCarousels(event.target);
    initQuantityControls(event.target);
    initProductGalleries(event.target);
    initProductRecommendations(event.target);
    initCollectionPages(event.target);
    initCartForms(event.target);
    initCartAddForms(event.target);
    initProductDetailVariants(event.target);
    var collectionSection = event.target.matches(
      "[data-collection-section]",
    )
      ? event.target
      : event.target.querySelector("[data-collection-section]");

    if (collectionSection) {
      applyCollectionNaturalSort(collectionSection, window.location.href);
    }
    initProductCardVariants(event.target);
  });

  document.addEventListener("shopify:section:unload", function (event) {
    var marquee = event.target.querySelector("[data-infinite-carousel]");

    if (marquee && typeof marquee.infiniteCarouselDestroy === "function") {
      marquee.infiniteCarouselDestroy();
    }
  });

  window.addEventListener("popstate", function () {
    var section = document.querySelector("[data-collection-section]");

    if (section) {
      updateCollection(section, window.location.href, {
        updateHistory: false,
        fallbackToNavigation: false,
      });
    }
  });

  document.addEventListener("theme:cart:open", openCart);
  document.addEventListener("theme:cart:close", closeCart);
  document.addEventListener("theme:cart:refresh", function () {
    refreshCart().catch(console.error);
  });

  window.theme = window.theme || {};
  window.theme.initSwipers = initSwipers;
  window.theme.initInfiniteCarousels = initInfiniteCarousels;
  window.theme.initQuantityControls = initQuantityControls;
  window.theme.initProductGalleries = initProductGalleries;
  window.theme.initProductRecommendations = initProductRecommendations;
  window.theme.initCollectionPages = initCollectionPages;
  window.theme.initCartForms = initCartForms;
  window.theme.initCartAddForms = initCartAddForms;
  window.theme.initProductCardVariants = initProductCardVariants;
  window.theme.openCart = openCart;
  window.theme.closeCart = closeCart;
  window.theme.refreshCart = refreshCart;
})();
